import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const TooltipContainer = styled.span`
  position: relative;
  display: inline-flex;
  margin-left: 6px;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #007bff;
  color: #ffffff;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #115e96;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TooltipBox = styled.div`
  position: fixed;
  min-width: 200px;
  max-width: 350px;
  padding: 8px 12px;
  background-color: #333;
  color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  word-wrap: break-word;
  white-space: normal;
  text-align: left;
  line-height: 1.4;
  font-size: 13px;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: ${(props) => (props.isOpen ? 'auto' : 'none')};

  /* Posizionamento dinamico calcolato in JS */
  top: ${(props) => props.position?.top || 0}px;
  left: ${(props) => props.position?.left || 0}px;

  /* Freccia del tooltip */
  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: ${(props) => props.arrowPosition || 50}%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom-color: #333;
  }
`;

function InfoTooltipIcon({ id, tooltipKey }) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState(50);
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  const displayText = !tooltipKey ? 'Informazione non disponibile' : tooltipKey;

  const handleClick = (e) => {
    e.stopPropagation();
    setTooltipOpen(!tooltipOpen);
  };

  useEffect(() => {
    if (tooltipOpen && iconRef.current && tooltipRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const scrollY = window.scrollY || window.pageYOffset;

      const top = iconRect.bottom + 8 + scrollY;

      let left = iconRect.left + iconRect.width / 2 - tooltipRect.width / 2;
      let arrow = 50;

      const MARGIN = 10;

      if (left + tooltipRect.width > viewportWidth - MARGIN) {
        left = viewportWidth - tooltipRect.width - MARGIN;
        const iconCenter = iconRect.left + iconRect.width / 2;
        arrow = ((iconCenter - left) / tooltipRect.width) * 100;
      } else if (left < MARGIN) {
        left = MARGIN;
        const iconCenter = iconRect.left + iconRect.width / 2;
        arrow = ((iconCenter - left) / tooltipRect.width) * 100;
      }

      setTooltipPosition({ top, left });
      setArrowPosition(arrow);
    }
  }, [tooltipOpen]);

  useEffect(() => {
    if (tooltipOpen) {
      const handleScroll = () => {
        setTooltipOpen(false);
      };

      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [tooltipOpen]);

  return (
    <TooltipContainer ref={iconRef}>
      <InfoIcon onClick={handleClick}>i</InfoIcon>
      <TooltipBox
        ref={tooltipRef}
        isOpen={tooltipOpen}
        position={tooltipPosition}
        arrowPosition={arrowPosition}
      >
        {displayText}
      </TooltipBox>
    </TooltipContainer>
  );
}

export default InfoTooltipIcon;
